import { useState, useEffect } from 'react'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

export default function Relatorios() {
    const { user, profile } = useAuth()
    const isMaster = profile?.user_role === 'master'

    const [deals, setDeals] = useState([])
    const [mesSelecionado, setMesSelecionado] = useState(new Date().toISOString().slice(0, 7))
    const [mesPerdidos, setMesPerdidos] = useState('')
    const [empresas, setEmpresas] = useState([])
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null)

    useEffect(() => {
        if (isMaster) {
            api.get('/companies').then(r => setEmpresas(r.data))
        }
    }, [isMaster])

    useEffect(() => {
        if (!user) return
        const params = {}
        if (isMaster) {
            params.is_master = true
            if (empresaSelecionada) params.company_id = empresaSelecionada
        } else {
            params.is_master = false
            params.company_id = profile?.company_id
        }
        api.get('/deals', { params }).then(r => setDeals(r.data))
    }, [user, isMaster, empresaSelecionada, profile])

    const wonDeals = deals.filter(d => {
        if (d.status !== 'won') return false
        const mes = new Date(d.updated_at).toISOString().slice(0, 7)
        return mes === mesSelecionado
    })

    const allLostDeals = deals.filter(d => d.status === 'lost')

    const lostDeals = allLostDeals.filter(d => {
        if (!mesPerdidos) return true
        const mes = new Date(d.updated_at).toISOString().slice(0, 7)
        return mes === mesPerdidos
    })

    const receitaMes = wonDeals.reduce((s, d) => s + Number(d.value || 0), 0)
    const totalPerdido = lostDeals.reduce((s, d) => s + Number(d.value || 0), 0)

    const motivosAgrupados = lostDeals.reduce((acc, d) => {
        const motivo = d.motivo_perda?.trim() || 'Sem motivo informado'
        if (!acc[motivo]) acc[motivo] = { count: 0, valor: 0 }
        acc[motivo].count++
        acc[motivo].valor += Number(d.value || 0)
        return acc
    }, {})

    const motivosOrdenados = Object.entries(motivosAgrupados)
        .sort((a, b) => b[1].count - a[1].count)

    const reativate = async (id) => {
        await api.put(`/deals/${id}`, { status: 'open', user_id: user.id, user_name: profile?.name })
        setDeals(prev => prev.map(d => d.id === id ? { ...d, status: 'open' } : d))
    }

    const empresaAtual = empresas.find(e => e.id === empresaSelecionada)

    return (
        <div className="space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold">Relatórios</h1>
                {isMaster && (
                    <div className="flex items-center gap-2">
                        <select
                            className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500"
                            value={empresaSelecionada || ''}
                            onChange={e => setEmpresaSelecionada(e.target.value || null)}
                        >
                            <option value="">Todas as empresas</option>
                            {empresas.map(e => (
                                <option key={e.id} value={e.id}>{e.name}</option>
                            ))}
                        </select>
                        {empresaSelecionada && (
                            <button onClick={() => setEmpresaSelecionada(null)} className="text-xs text-gray-500 hover:text-white transition">✕</button>
                        )}
                    </div>
                )}
            </div>

            {empresaAtual && (
                <p className="text-indigo-400 text-sm">🏢 Visualizando: {empresaAtual.name}</p>
            )}

            {/* Negócios Ganhos */}
            <div>
                <div className="flex items-center gap-4 mb-4">
                    <h2 className="text-lg font-semibold text-emerald-400">✓ Negócios Ganhos</h2>
                    <input
                        type="month"
                        value={mesSelecionado}
                        onChange={e => setMesSelecionado(e.target.value)}
                        className="bg-gray-800 text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-2 ring-indigo-500"
                    />
                </div>

                <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-4 mb-4 flex items-center justify-between">
                    <div>
                        <p className="text-gray-400 text-sm">Receita do mês</p>
                        <p className="text-3xl font-bold text-emerald-400 mt-1">R$ {receitaMes.toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="text-right">
                        <p className="text-gray-400 text-sm">Negócios fechados</p>
                        <p className="text-3xl font-bold text-white mt-1">{wonDeals.length}</p>
                    </div>
                </div>

                <div className="space-y-2">
                    {wonDeals.map(d => (
                        <div key={d.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex justify-between items-center">
                            <div>
                                <p className="font-semibold">{d.title}</p>
                                <div className="flex flex-wrap gap-3 mt-1">
                                    {d.produto && <span className="text-xs text-indigo-400">{d.produto}</span>}
                                    {d.contato && <span className="text-xs text-gray-400">👤 {d.contato}</span>}
                                    {d.segmento && <span className="text-xs text-gray-400">🏷️ {d.segmento}</span>}
                                    {d.origem && <span className="text-xs text-yellow-400">{d.origem}</span>}
                                </div>
                            </div>
                            <span className="text-emerald-400 font-bold ml-4 whitespace-nowrap">
                                {d.value ? `R$ ${Number(d.value).toLocaleString('pt-BR')}` : '—'}
                            </span>
                        </div>
                    ))}
                    {wonDeals.length === 0 && <p className="text-gray-500 text-center py-8">Nenhum negócio ganho neste mês.</p>}
                </div>
            </div>

            {/* Motivos de Perda */}
            {allLostDeals.length > 0 && (
                <div>
                    <div className="flex items-center gap-4 mb-4">
                        <h2 className="text-lg font-semibold text-orange-400">📊 Motivos de Perda</h2>
                        <input
                            type="month"
                            value={mesPerdidos}
                            onChange={e => setMesPerdidos(e.target.value)}
                            className="bg-gray-800 text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-2 ring-indigo-500"
                        />
                        {mesPerdidos && (
                            <button onClick={() => setMesPerdidos('')} className="text-xs text-gray-500 hover:text-white transition">✕ Todos</button>
                        )}
                    </div>

                    <div className="bg-red-900/10 border border-red-800/40 rounded-xl p-4 mb-4 flex items-center justify-between">
                        <div>
                            <p className="text-gray-400 text-sm">Total de deals perdidos {mesPerdidos ? 'no mês' : 'no geral'}</p>
                            <p className="text-3xl font-bold text-red-400 mt-1">{lostDeals.length}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-gray-400 text-sm">Valor total perdido</p>
                            <p className="text-3xl font-bold text-red-300 mt-1">R$ {totalPerdido.toLocaleString('pt-BR')}</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {motivosOrdenados.map(([motivo, dados]) => {
                            const pct = Math.round((dados.count / lostDeals.length) * 100)
                            return (
                                <div key={motivo} className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                                    <div className="flex justify-between items-start mb-2">
                                        <p className="text-sm font-semibold text-white flex-1 mr-4">{motivo}</p>
                                        <div className="text-right shrink-0">
                                            <span className="text-xs text-red-400 font-bold">{dados.count}x</span>
                                            {dados.valor > 0 && (
                                                <p className="text-xs text-gray-400 mt-0.5">R$ {dados.valor.toLocaleString('pt-BR')}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-800 rounded-full h-1.5">
                                        <div className="bg-red-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">{pct}% dos deals perdidos</p>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {/* Base de Recuperação */}
            <div>
                <div className="flex items-center gap-4 mb-4">
                    <h2 className="text-lg font-semibold text-red-400">✕ Base de Recuperação ({lostDeals.length})</h2>
                    {!mesPerdidos && <span className="text-xs text-gray-500">Use o filtro acima para filtrar por mês</span>}
                </div>
                <div className="space-y-2">
                    {lostDeals.map(d => (
                        <div key={d.id} className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex justify-between items-center">
                            <div className="flex-1">
                                <p className="font-semibold">{d.title}</p>
                                <div className="flex flex-wrap gap-3 mt-1">
                                    {d.produto && <span className="text-xs text-indigo-400">{d.produto}</span>}
                                    {d.contato && <span className="text-xs text-gray-400">👤 {d.contato}</span>}
                                    {d.segmento && <span className="text-xs text-gray-400">🏷️ {d.segmento}</span>}
                                    {d.site && <span className="text-xs text-gray-400">🌐 {d.site}</span>}
                                </div>
                                {d.motivo_perda && (
                                    <p className="text-xs text-red-400 mt-1">❌ Motivo: <span className="text-gray-300">{d.motivo_perda}</span></p>
                                )}
                                {d.observacoes && <p className="text-xs text-gray-500 mt-1 italic">"{d.observacoes}"</p>}
                            </div>
                            <button
                                onClick={() => reativate(d.id)}
                                className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-3 py-1.5 rounded-lg transition font-semibold ml-4 whitespace-nowrap"
                            >
                                ↩ Reativar
                            </button>
                        </div>
                    ))}
                    {lostDeals.length === 0 && <p className="text-gray-500 text-center py-8">Nenhum cliente perdido neste período. 🎉</p>}
                </div>
            </div>
        </div>
    )
}